/**
 * UI-42 / PILOT-22: SLA Breach Visibility
 *
 * Displays backend SLA breach data directly — no recomputation, no derivation.
 * Maps backend values deterministically:
 *   breachedCount            → displayed as-is
 *   oldestRequestMinutes     → Math.round() display only
 *   oldestBreachedRoomNumber → shown when available
 *
 * When breaches exist, the panel is clickable and navigates to the
 * Requests view pre-filtered to SLA-breached requests.
 *
 * Always visible (never hidden or collapsed).
 */

import { useNavigate } from 'react-router-dom';
import type { SlaRisk } from '../../services/controlTowerTypes';

interface SLABreachDisplayProps {
  slaRisk: SlaRisk;
}

export function SLABreachDisplay({ slaRisk }: SLABreachDisplayProps) {
  const navigate = useNavigate();
  const { breachedCount, oldestRequestMinutes, oldestBreachedRoomNumber } = slaRisk;

  const hasBreaches = breachedCount > 0;

  function handleClick() {
    navigate('/requests?filter=sla-breached');
  }

  const sharedClass = `px-4 py-2.5 bg-slate-800/60 border rounded-xl transition-colors ${
    hasBreaches
      ? 'border-rose-700/60 hover:bg-rose-900/20 hover:border-rose-600/70'
      : 'border-slate-700/50'
  }`;

  const inner = (
    <>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-0.5">SLA Breaches</p>
      {!hasBreaches ? (
        <p className="text-sm text-slate-200">No SLA breaches</p>
      ) : (
        <>
          <p className="text-sm text-rose-300 font-medium">
            {breachedCount} request{breachedCount !== 1 ? 's' : ''} past SLA
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {oldestBreachedRoomNumber
              ? `Oldest: Room ${oldestBreachedRoomNumber} · ${Math.round(oldestRequestMinutes)} min — tap to view`
              : `Oldest: ${Math.round(oldestRequestMinutes)} min — tap to view`}
          </p>
        </>
      )}
    </>
  );

  if (hasBreaches) {
    return (
      <button
        type="button"
        className={`w-full text-left ${sharedClass}`}
        onClick={handleClick}
        aria-label="View SLA-breached requests"
      >
        {inner}
      </button>
    );
  }

  return <div className={sharedClass}>{inner}</div>;
}
